import { z } from 'zod';
import { SCENE_REFERENCE_INSTRUCTION, SCENE_REFERENCE_LIMIT, sceneReferencesFit, } from './scene-references.js';
const ScenePromptCharacterSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    description: z.string(),
    isLead: z.boolean(),
});
const ScenePromptThingSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    description: z.string(),
});
const ScenePromptLocationSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    description: z.string(),
});
export const ScenePromptContextSchema = z.object({
    target: z.discriminatedUnion('kind', [
        z.object({ kind: z.literal('scene'), sceneId: z.string().uuid() }),
        z.object({
            kind: z.literal('draft'),
            draftId: z.string().uuid(),
            spreadIndex: z.number().int().nonnegative(),
        }),
    ]),
    sourceText: z.string().min(1),
    sourceTextHash: z.string().regex(/^[a-f0-9]{64}$/u),
    previousText: z.string(),
    nextText: z.string(),
    currentPromptEn: z.string(),
    language: z.string().min(2).max(16),
    characters: z.array(ScenePromptCharacterSchema),
    things: z.array(ScenePromptThingSchema),
    locations: z.array(ScenePromptLocationSchema),
});
export const ScenePromptOutputSchema = z
    .object({
    promptEn: z.string().trim().min(1).max(10_000),
    animationEn: z.string().trim().min(1).max(10_000),
    characterIds: z.array(z.string().uuid()),
    thingIds: z.array(z.string().uuid()),
    locationId: z.string().uuid().nullable(),
})
    .superRefine((scene, ctx) => {
    if (!sceneReferencesFit(scene)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `A spread must fit ${SCENE_REFERENCE_LIMIT} images while keeping characters separate and reserving a slot for any objects and location`,
        });
    }
});
/**
 * Builds the complete LLM request for one spread after its reader-facing text changed.
 * Style and layout are deliberately absent: buildScenePrompt adds those deterministic
 * parts later, while this request is responsible only for visual content and references.
 */
export function buildScenePromptBrief(context) {
    const parsed = ScenePromptContextSchema.parse(context);
    const systemPrompt = [
        "You are the visual continuity editor for a personalized children's picture book.",
        'Regenerate the visual description for exactly one spread from its current reader-facing text.',
        'Return English visual prompts, regardless of the language of the story text.',
        'Describe only visible subjects, actions, setting, time, weather, mood, camera framing, and continuity details.',
        'Do not include art style, aspect ratio, layout instructions, typography, dialogue, captions, or text in the image.',
        'Select every character and listed object that is visibly present. Select a listed location only when the action physically occurs there.',
        // The text may name family the cast lacks ("a cake for Mom's birthday"): we have no
        // likeness of them, and a picture of them comes out as a stranger.
        'People and pets the child knows in real life (family, friends, their own pets, a teacher) are drawn only if they are in the Characters catalog; anyone of that kind the text mentions who is not there stays out of the picture. People who could look any way (a shopkeeper, a new friend, a dragon) may be drawn.',
        SCENE_REFERENCE_INSTRUCTION,
        'Use only IDs from the supplied catalogs. Never invent an ID. Use null when the setting is not one of the supplied locations.',
        'The animation prompt must describe subtle natural motion for the same composition and must not introduce new subjects.',
    ].join(' ');
    const catalog = (items) => items.length === 0
        ? '— none'
        : items
            .map((item) => `— ${item.id}: ${item.name}; ${item.description || 'no description'}`)
            .join('\n');
    const characters = parsed.characters.length === 0
        ? '— none'
        : parsed.characters
            .map((item) => `— ${item.id}: ${item.name}; ${item.description || 'no description'}${item.isLead ? '; lead character' : ''}`)
            .join('\n');
    const userPrompt = [
        `Story language: ${parsed.language}`,
        `Previous spread (context only): ${parsed.previousText || 'none'}`,
        `CURRENT SPREAD — source of truth: ${parsed.sourceText}`,
        `Next spread (context only): ${parsed.nextText || 'none'}`,
        `Previous visual prompt (continuity hint only; rewrite it to match the current spread): ${parsed.currentPromptEn || 'none'}`,
        `Characters:\n${characters}`,
        `Objects:\n${catalog(parsed.things)}`,
        `Locations:\n${catalog(parsed.locations)}`,
        'Return one JSON object that matches the schema. Copy selected IDs exactly from the catalogs.',
    ].join('\n\n');
    return { context: parsed, systemPrompt, userPrompt };
}
export function validateScenePromptOutput(value, context) {
    const parsed = ScenePromptOutputSchema.safeParse(value);
    if (!parsed.success)
        return { success: false, issue: parsed.error.message };
    const characterIds = new Set(context.characters.map((item) => item.id));
    const thingIds = new Set(context.things.map((item) => item.id));
    const locationIds = new Set(context.locations.map((item) => item.id));
    const unknownCharacter = parsed.data.characterIds.find((id) => !characterIds.has(id));
    if (unknownCharacter)
        return { success: false, issue: `Unknown character id: ${unknownCharacter}` };
    const unknownThing = parsed.data.thingIds.find((id) => !thingIds.has(id));
    if (unknownThing)
        return { success: false, issue: `Unknown thing id: ${unknownThing}` };
    if (parsed.data.locationId && !locationIds.has(parsed.data.locationId)) {
        return { success: false, issue: `Unknown location id: ${parsed.data.locationId}` };
    }
    if (new Set(parsed.data.characterIds).size !== parsed.data.characterIds.length) {
        return { success: false, issue: 'characterIds contains duplicates' };
    }
    if (new Set(parsed.data.thingIds).size !== parsed.data.thingIds.length) {
        return { success: false, issue: 'thingIds contains duplicates' };
    }
    return { success: true, data: parsed.data };
}
/** Provider-neutral JSON Schema used by OpenAI and Anthropic structured outputs. */
export const scenePromptOutputJsonSchema = {
    type: 'object',
    description: SCENE_REFERENCE_INSTRUCTION,
    additionalProperties: false,
    properties: {
        promptEn: { type: 'string' },
        animationEn: { type: 'string' },
        characterIds: { type: 'array', items: { type: 'string' } },
        thingIds: { type: 'array', items: { type: 'string' } },
        locationId: { anyOf: [{ type: 'string' }, { type: 'null' }] },
    },
    required: ['promptEn', 'animationEn', 'characterIds', 'thingIds', 'locationId'],
};
//# sourceMappingURL=scene-prompt.js.map